import { Cell } from '@tanstack/react-table';

import { ColumnTypesInternal } from 'support/types/webediTable';

import { isActionColumn } from '../../hooks/useIsActionColumn';
import { getEmbeddedTableRowRowSpan } from '../../helpers';
import { EmbeddedTableRowRowSpan, RowWithEmbeddedTableRowInfo } from '../../types';

export type CellBasicProperties = {
  isAction: boolean;
  fixedWidth: string | undefined;
  isFirstColumn: boolean;
  isLastColumn: boolean;
};

export type RowSpanResult = {
  shouldSkipRendering: boolean;
  rowSpan: number | undefined;
};

/**
 * What a cell renders as on this embedded row.
 *
 * `skip` means a neighbouring cell already spans this position, so returning nothing lets the
 * browser fill it via rowSpan/colSpan. `groupSummary` is the single wide cell that stands in for a
 * whole column group — the add row in create/edit, or the collapsed summary in view.
 */
export type CellRenderPlan =
  | { kind: 'skip' }
  | {
      kind: 'groupSummary';
      variant: 'addRow' | 'collapsed';
      colSpan: number;
      rowSpan: number | undefined;
      itemsCount: number;
      groupPath: string;
    }
  | { kind: 'cell'; rowSpan: number | undefined };

export const planCellRender = <TData>(
  cell: Cell<TData, unknown>,
  index: number,
  visibleCells: Array<Cell<TData, unknown>>,
  rowWithEmbeddedTableRowInfo: RowWithEmbeddedTableRowInfo<TData> | undefined,
): CellRenderPlan => {
  const groupSummary = resolveGroupSummary(cell, rowWithEmbeddedTableRowInfo);

  // The group's first non-toggle sub-column carries the wide cell; the rest is spanned into. The
  // toggle keeps its own cell, or a collapsed group could never be reopened.
  if (groupSummary && index > 0 && isSameGroupDataCell(visibleCells[index - 1], groupSummary.groupPath)) {
    return { kind: 'skip' };
  }

  const { shouldSkipRendering, rowSpan } = calculateCellRowSpan(cell, rowWithEmbeddedTableRowInfo);

  if (shouldSkipRendering) {
    return { kind: 'skip' };
  }

  return groupSummary ? { ...groupSummary, rowSpan } : { kind: 'cell', rowSpan };
};

export const calculateCellBasicProperties = <TData, TValue>(
  cell: Cell<TData, TValue>,
  index: number,
  totalCells: number,
): CellBasicProperties => ({
  isAction: isActionColumn(cell.column.columnDef),
  fixedWidth: cell.column.columnDef.meta?.style?.width,
  isFirstColumn: !index,
  isLastColumn: index === totalCells - 1,
});

/**
 * Determines if a cell should render and its rowSpan value.
 *
 * Basic columns (non-array): span all embedded table rows, render only on first embedded table row
 * Array columns: use LCM-based spanning with compressed row heights
 * Regular columns: no spanning
 *
 * The collapse toggle sits inside a group but is not per-item: it would otherwise repeat once per
 * array item, so it spans the whole row like a column with no group.
 */
export const calculateCellRowSpan = <TData>(
  cell: Cell<TData, unknown>,
  rowWithEmbeddedTableRowInfo: RowWithEmbeddedTableRowInfo<TData> | undefined,
): RowSpanResult => {
  const parentColumnPath = cell.column.parent?.id;
  const isBasicColumn = rowWithEmbeddedTableRowInfo && (!parentColumnPath || isCollapseToggle(cell));

  if (isBasicColumn) {
    const shouldSkipRendering = !rowWithEmbeddedTableRowInfo.isFirstEmbeddedTableRow;
    const rowSpan = rowWithEmbeddedTableRowInfo.isFirstEmbeddedTableRow
      ? rowWithEmbeddedTableRowInfo.embeddedTableRowsCount
      : undefined;

    return { shouldSkipRendering, rowSpan };
  }

  const embeddedTableRowRowSpan = getEmbeddedTableRowRowSpan(rowWithEmbeddedTableRowInfo, parentColumnPath);

  if (embeddedTableRowRowSpan && rowWithEmbeddedTableRowInfo) {
    return calculateEmbeddedTableRowRowSpan(embeddedTableRowRowSpan, rowWithEmbeddedTableRowInfo);
  }

  return { shouldSkipRendering: false, rowSpan: undefined };
};

export const calculateCellStyle = (
  isAction: boolean,
  columnsWidth: string,
  fixedWidth: string | undefined,
): React.CSSProperties | undefined => (!isAction ? { width: columnsWidth, maxWidth: fixedWidth } : undefined);

/**
 * Calculates rowSpan for embedded table row cells using the LCM approach.
 *
 * Each array item spans multiple rows (rowsPerItem). Only the first row
 * for each item actually renders the cell - subsequent rows skip rendering
 * because the browser extends the cell automatically via rowSpan.
 *
 * Example: 6 total rows, 3 items → rowsPerItem=2
 * - Item 0: renders at row 0 with rowSpan=2, skips row 1
 * - Item 1: renders at row 2 with rowSpan=2, skips row 3
 * - Item 2: renders at row 4 with rowSpan=2, skips row 5
 */
const calculateEmbeddedTableRowRowSpan = <TData>(
  embeddedTableRowRowSpan: EmbeddedTableRowRowSpan,
  rowWithEmbeddedTableRowInfo: RowWithEmbeddedTableRowInfo<TData>,
): RowSpanResult => {
  if (!embeddedTableRowRowSpan.totalItems) {
    const { isFirstEmbeddedTableRow } = rowWithEmbeddedTableRowInfo;

    if (!isFirstEmbeddedTableRow) {
      return { shouldSkipRendering: true, rowSpan: undefined };
    }

    return { shouldSkipRendering: false, rowSpan: rowWithEmbeddedTableRowInfo.embeddedTableRowsCount };
  }

  // Calculate how many rows each item spans
  const rowSpan = rowWithEmbeddedTableRowInfo.embeddedTableRowsCount / embeddedTableRowRowSpan.totalItems;

  // Determine which item should be shown at this row position
  const itemIndex = Math.floor(rowWithEmbeddedTableRowInfo.embeddedTableRowIndex / rowSpan);

  // Only render on the first row for this item (other rows are spanned into)
  const isFirstRowForThisItem = rowWithEmbeddedTableRowInfo.embeddedTableRowIndex === itemIndex * rowSpan;

  if (!isFirstRowForThisItem) {
    return { shouldSkipRendering: true, rowSpan: undefined };
  }

  return { shouldSkipRendering: false, rowSpan };
};

/**
 * The wide cell a column group renders instead of its items, if any: the add row in create/edit, or
 * the summary of a collapsed group in any mode. `rowSpan` is filled in by the caller, which is what
 * decides whether the cell renders at all.
 */
const resolveGroupSummary = <TData>(
  cell: Cell<TData, unknown>,
  rowWithEmbeddedTableRowInfo: RowWithEmbeddedTableRowInfo<TData> | undefined,
): Omit<Extract<CellRenderPlan, { kind: 'groupSummary' }>, 'rowSpan'> | undefined => {
  const groupPath = cell.column.parent?.id;
  const groupRowSpan = getEmbeddedTableRowRowSpan(rowWithEmbeddedTableRowInfo, groupPath);

  if (!groupPath || !groupRowSpan || isCollapseToggle(cell)) {
    return undefined;
  }

  const variant = groupRowSpan.isAddRow ? 'addRow' : groupRowSpan.isCollapsedSummary ? 'collapsed' : undefined;

  if (!variant) {
    return undefined;
  }

  return {
    kind: 'groupSummary',
    variant,
    colSpan: groupRowSpan.colSpan || 1,
    itemsCount: groupRowSpan.collapsedItemsCount ?? 0,
    groupPath,
  };
};

const isCollapseToggle = <TData>(cell: Cell<TData, unknown>): boolean =>
  cell.column.columnDef.meta?.type === ColumnTypesInternal.procuros_collapse;

const isSameGroupDataCell = <TData>(cell: Cell<TData, unknown>, groupPath: string | undefined): boolean =>
  cell.column.parent?.id === groupPath && !isCollapseToggle(cell);
